import { Dispatch, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import lpService from "@services/landing.pages.service";
import { TRequestStatus } from "@models/msc.model";
import { TLandingPage } from "@/models/landing-pages.model";
import { RootState } from "..";
import { AB_TEST_STATUS_PAUSED, AB_TEST_STATUS_RUNNING } from "@icebergeradmin/common";

type LPState = {
  initialized: boolean;
  status: TRequestStatus;
  pages: TLandingPage[];
  error: string;
};

//-----THUNK FUNCTIONS-----
export const createLandingPage = createAsyncThunk("lp/generate", lpService.generateLPService);

export const updateLandingPage = createAsyncThunk("lp/update", lpService.updateLandingPageService);

const initialState: LPState = {
  initialized: false,
  status: "IDLE",
  pages: [],
  error: "",
};

const lpSlice = createSlice({
  name: "lp",
  initialState,
  reducers: {
    startLoading(state) {
      state.status = "PENDING";
    },
    gotPages(state, action) {
      state.pages = action.payload;
      state.status = "IDLE";
      state.initialized = true;
    },
    pagePublished(state, action) {
      state.pages = state.pages.map((i) => (i.id === action.payload ? { ...i, published: true } : i));
    },
    pageUnpublished(state, action) {
      state.pages = state.pages.map((i) => (i.id === action.payload ? { ...i, published: false } : i));
    },
    pageRemoved(state, action) {
      state.pages = state.pages.filter((i) => i.id !== action.payload);
    },
    pageDuplicated(state, action) {
      state.pages = [{ ...action.payload }, ...state.pages];
    },
    pageCreated(state, action) {
      state.pages = [{ ...action.payload }, ...state.pages];
    },
    rollBack(state, action) {
      state.pages = action.payload;
    },
    splitTestCreated(state, action) {
      state.pages = state.pages.map((i) =>
        i.id === action.payload ? { ...i, hasAbTest: true, abTestStatus: AB_TEST_STATUS_RUNNING } : i
      );
    },
    splitTestStopped(state, action) {
      state.pages = state.pages.map((i) =>
        i.id === action.payload ? { ...i, hasAbTest: false, abTestStatus: AB_TEST_STATUS_PAUSED } : i
      );
    },
  },
  extraReducers: (builder) => {
    /*--------------------------------------------------*/
    builder.addCase(createLandingPage.rejected, (state) => {
      state.status = "REJECTED";
    });

    builder.addCase(createLandingPage.pending, (state) => {
      state.status = "PENDING";
    });

    builder.addCase(createLandingPage.fulfilled, (state, action) => {
      const createdPage = action.payload;
      state.pages.unshift(createdPage);
    });
    /*--------------------------------------------------*/
    builder.addCase(updateLandingPage.rejected, (_, action) => {
      throw new Error(action.error.message);
    });
    builder.addCase(updateLandingPage.fulfilled, (state, action) => {
      const { payload } = action;
      const pageIndex = state.pages.findIndex((item) => item.id === payload.id);
      if (pageIndex === -1) return;
      state.pages[pageIndex] = payload;
    });
  },
});

//  ACTIONS
export const { splitTestCreated, splitTestStopped } = lpSlice.actions;

export const getLandingPages = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const {
    lp: { pages, initialized },
  } = getState();
  if (initialized) return pages;
  dispatch(lpSlice.actions.startLoading());
  const response = await lpService.getLandingPagesService();
  dispatch(lpSlice.actions.gotPages(response));
  return response;
};

export const publishLanginPage = (pageId: number) => async (dispatch: Dispatch) => {
  dispatch(lpSlice.actions.pagePublished(pageId));
  try {
    await lpService.publishLandingPageService(pageId);
  } catch (e) {
    dispatch(lpSlice.actions.pageUnpublished(pageId));
    throw e;
  }
};

export const unpublishLanginPage = (pageId: number) => async (dispatch: Dispatch) => {
  dispatch(lpSlice.actions.pageUnpublished(pageId));
  try {
    await lpService.unpublishLandingPageService(pageId);
  } catch (e) {
    dispatch(lpSlice.actions.pagePublished(pageId));
    throw e;
  }
};

export const deleteLandingPage = (pageId: number) => async (dispatch: Dispatch, getState: () => RootState) => {
  const pagesForRollBack = structuredClone(getState().lp.pages);

  dispatch(lpSlice.actions.pageRemoved(pageId));
  try {
    await lpService.deleteLandingPageService(pageId);
  } catch (e) {
    dispatch(lpSlice.actions.rollBack(pagesForRollBack));
    throw e;
  }
};

export const duplicatePage = (pageId: number) => async (dispatch: Dispatch) => {
  const page = await lpService.duplicatePageService(pageId);
  dispatch(lpSlice.actions.pageDuplicated(page));
};

export const createThankyouPage = () => async (dispatch: Dispatch) => {
  const page = await lpService.createThankyouPage();
  dispatch(lpSlice.actions.pageCreated(page));
  return page;
};

// ----------------------------

export default lpSlice.reducer;
